"use client";

import { useId } from "react";

import { IconCheck } from "./parts";

/*
 * 입력 부품 — 시안 08·09 의 입력칸(54 · 모서리 md · 옅은 회색 바탕 · 18px)과 체크 줄.
 *
 * 🔴 **입력칸 글자는 16px 이상입니다**(시안 18) — iOS Safari 는 16px 미만이면 초점 때 화면을 확대합니다.
 * 🔴 **체크박스는 네이티브 `<input type="checkbox">`** 입니다(README §12 검수표). 눈에 보이는 네모는
 *    그림이고 실제 입력은 투명하게 겹쳐 두어 Tab·스페이스·낭독기가 그대로 먹습니다. 초점 고리는
 *    그림 쪽(`peer-focus-visible`)에 그립니다.
 */
export function TextField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  error,
  hint,
  optional = false,
  required = false,
  autoComplete,
  inputMode,
  trailing,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "email" | "password" | "tel";
  placeholder?: string;
  error?: string;
  hint?: string;
  /** 라벨 옆 «선택» */
  optional?: boolean;
  required?: boolean;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  /** 입력칸 오른쪽 버튼(시안 09 «인증번호») */
  trailing?: React.ReactNode;
  disabled?: boolean;
}) {
  const id = useId();
  const describedBy = [error ? `${id}-error` : "", hint ? `${id}-hint` : ""].filter(Boolean).join(" ") || undefined;

  /* 🔑 `min-w-0` — 격자 칸은 기본 최소 폭이 내용 폭이라, 없으면 입력칸이 카드 밖으로 밀립니다. */
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-2 block text-[length:calc(var(--tnf-text-base)*13.5/14)] font-medium text-ink-faint">
        {label}
        {optional && <span className="ml-1 font-medium text-ink-faint/70">선택</span>}
      </label>
      <div className="flex min-w-0 gap-[7px]">
        <input
          id={id}
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
          inputMode={inputMode}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={`simple-input h-[54px] min-w-0 flex-1 rounded-[var(--tnf-radius-sm)] border bg-raised px-3.5 text-[length:max(16px,calc(var(--tnf-text-base)*18/14))] font-medium text-ink outline-none placeholder:text-ink-faint/60 disabled:opacity-60 ${error ? "border-danger" : "border-line"}`}
        />
        {trailing}
      </div>
      {hint && !error && <p id={`${id}-hint`} className="mx-0.5 mt-2 text-caption text-ink-faint">{hint}</p>}
      {error && <p id={`${id}-error`} role="alert" className="mx-0.5 mt-2 text-caption font-bold text-danger">{error}</p>}
    </div>
  );
}

/** 체크 줄 — 시안 09 의 동의 항목. `after` 는 오른쪽 «보기» 자리입니다. */
export function CheckRow({
  checked,
  onChange,
  children,
  after,
  strong = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: React.ReactNode;
  after?: React.ReactNode;
  /** «전체 동의» — 글자가 큽니다 */
  strong?: boolean;
}) {
  const id = useId();
  return (
    <div className={`flex items-center gap-2.5 ${strong ? "px-0.5 pt-0.5 pb-[15px]" : "px-0.5 py-[11px]"}`}>
      <label htmlFor={id} className="relative flex min-w-0 flex-1 cursor-pointer items-center gap-[11px]">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="peer absolute top-0 left-0 size-6 cursor-pointer opacity-0"
        />
        <span
          aria-hidden="true"
          className={`grid size-6 flex-none place-items-center rounded-[var(--tnf-radius-xxs)] text-on-accent peer-focus-visible:outline-3 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent ${
            checked ? "bg-accent" : "bg-surface shadow-[inset_0_0_0_1.5px_#d3d9e0]"
          }`}
        >
          {checked && <IconCheck />}
        </span>
        <span className={strong ? "text-[length:calc(var(--tnf-text-base)*16.5/14)] font-bold tracking-[-0.015em] text-ink" : "text-[length:calc(var(--tnf-text-base)*14.5/14)] text-ink-soft"}>{children}</span>
      </label>
      {after}
    </div>
  );
}
